import { useEffect, useState } from "react";
import { DateTime } from "luxon";
import { useQueryClient } from "@tanstack/react-query";
import {
  AdjustmentsHorizontalIcon,
  XMarkIcon,
  CheckCircleIcon,
  NoSymbolIcon,
  ExclamationTriangleIcon,
  CloudIcon,
  XCircleIcon,
  SparklesIcon,
  Cog6ToothIcon,
  QuestionMarkCircleIcon,
  PencilIcon,
  BoltIcon,
  Squares2X2Icon,
  MagnifyingGlassCircleIcon,
  RectangleGroupIcon,
  EllipsisHorizontalCircleIcon,
  ComputerDesktopIcon,
  PhotoIcon,
  ShieldCheckIcon,
  RectangleStackIcon,
} from "@heroicons/react/24/outline";
import Modal from "../../../layouts/Modal";
import { ButtonInput } from "../Inputs/ButtonInput";
import useNotes from "../../../data/useNotes";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import { INewNote, ResourceType } from "../../../models/Note";
import classNames from "../../../utils/classNames";

export const PART_COMPONENTS = ['PLUG', 'MOUNTS', 'LENS', 'BALLAST', 'BUTTONS', 'SCREEN', 'GRAPHICS', 'LEATHER', 'PANEL'] as const;
export const CONDITIONS = ['NONE', 'INTACT', 'DAMAGED', 'CLOUDY', 'BROKEN', 'RUSTED', 'CORRODED', 'MISSING', 'SCRATCHED THROUGHOUT'] as const;

type PartComponent = typeof PART_COMPONENTS[number];
type Condition = typeof CONDITIONS[number];
type Selections = Record<PartComponent, Condition>;

const CONDITION_META: Record<Condition, { icon: React.ElementType; textColor: string; selectedBg: string }> = {
  'NONE':                 { icon: NoSymbolIcon,           textColor: 'text-gray-400',   selectedBg: 'bg-gray-400'   },
  'INTACT':               { icon: CheckCircleIcon,        textColor: 'text-green-500',  selectedBg: 'bg-green-500'  },
  'DAMAGED':              { icon: ExclamationTriangleIcon,textColor: 'text-orange-400', selectedBg: 'bg-orange-400' },
  'CLOUDY':               { icon: CloudIcon,              textColor: 'text-blue-400',   selectedBg: 'bg-blue-400'   },
  'BROKEN':               { icon: XCircleIcon,            textColor: 'text-red-500',    selectedBg: 'bg-red-500'    },
  'RUSTED':               { icon: SparklesIcon,           textColor: 'text-amber-600',  selectedBg: 'bg-amber-600'  },
  'CORRODED':             { icon: Cog6ToothIcon,          textColor: 'text-purple-500', selectedBg: 'bg-purple-500' },
  'MISSING':              { icon: QuestionMarkCircleIcon, textColor: 'text-gray-400',   selectedBg: 'bg-gray-400'   },
  'SCRATCHED THROUGHOUT': { icon: PencilIcon,             textColor: 'text-yellow-600', selectedBg: 'bg-yellow-500' },
};

const PART_META: Record<PartComponent, { icon: React.ElementType }> = {
  'PLUG':     { icon: BoltIcon },
  'MOUNTS':   { icon: Squares2X2Icon },
  'LENS':     { icon: MagnifyingGlassCircleIcon },
  'BALLAST':  { icon: RectangleGroupIcon },
  'BUTTONS':  { icon: EllipsisHorizontalCircleIcon },
  'SCREEN':   { icon: ComputerDesktopIcon },
  'GRAPHICS': { icon: PhotoIcon },
  'LEATHER':  { icon: ShieldCheckIcon },
  'PANEL':    { icon: RectangleStackIcon },
};

function defaultSelections(): Selections {
  return Object.fromEntries(PART_COMPONENTS.map(p => [p, 'NONE'])) as Selections;
}

interface PartConditionPickerModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  resourceId: string;
  resourceType: ResourceType;
  refetchQuery?: any[];
}

export default function PartConditionPickerModal({
  open,
  setOpen,
  resourceId,
  resourceType,
  refetchQuery,
}: PartConditionPickerModalProps) {
  const { create } = useNotes();
  const { saveHandlers } = useDefaultCRUDHandlers("Note");
  const queryClient = useQueryClient();
  const [selections, setSelections] = useState<Selections>(defaultSelections());

  useEffect(() => {
    if (open) setSelections(defaultSelections());
  }, [open]);

  const handleSelect = (part: PartComponent, condition: Condition) => {
    setSelections(prev => ({ ...prev, [part]: condition }));
  };

  const previewNote = PART_COMPONENTS
    .filter(part => selections[part] !== 'NONE')
    .map(part => `${part} ${selections[part]}`)
    .join(' / ');

  const handleConfirm = () => {
    if (!previewNote) {
      setOpen(false);
      return;
    }
    const newNote: INewNote = {
      message: previewNote,
      resourceId,
      resourceType,
      date: DateTime.now().toISODate()!,
    };
    create.mutate(newNote, {
      onSuccess: () => {
        saveHandlers.onSuccess();
        // queryClient.refetchQueries(["notes", resourceId]);
        if (refetchQuery?.length) {
          setTimeout(() => queryClient.refetchQueries(refetchQuery), 200);
        }
        setOpen(false);
      },
      onError: saveHandlers.onError,
    });
  };

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-7xl">
      <div>

        {/* Header */}
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary-100 shrink-0">
              <AdjustmentsHorizontalIcon className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Part Condition Picker</h2>
              <p className="text-sm text-gray-500">
                Select the condition for each component. Rows left at <strong>NONE</strong> are excluded from the note.
              </p>
            </div>
          </div>
        </div>

        {/* Table — flex-1 columns fill full width, eliminating right gap */}
        <div className="w-full">

          {/* Column header row */}
          <div className="flex items-end pb-2 border-b border-gray-100">
            <div className="flex-none w-36" />
            {CONDITIONS.map(condition => {
              const { icon: Icon, textColor } = CONDITION_META[condition];
              return (
                <div key={condition} className="flex-1 flex flex-col items-center gap-1 px-1">
                  <Icon className={classNames("h-5 w-5 shrink-0", textColor)} />
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wide text-center leading-tight w-full">
                    {condition}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Part rows */}
          {PART_COMPONENTS.map((part, rowIdx) => {
            const { icon: PartIcon } = PART_META[part];
            const isLast = rowIdx === PART_COMPONENTS.length - 1;
            return (
              <div
                key={part}
                className={classNames(
                  "group flex items-center py-1.5 rounded-md transition-colors hover:bg-gray-50",
                  !isLast ? "border-b border-gray-100" : ""
                )}
              >
                {/* Part label */}
                <div className="flex-none w-36 flex items-center gap-2 pr-2">
                  <PartIcon className="h-4 w-4 shrink-0 text-gray-400 transition-colors group-hover:text-primary-500" />
                  <span className="text-sm font-semibold text-gray-700 transition-colors group-hover:text-primary-600">{part}</span>
                </div>

                {/* Condition pills */}
                {CONDITIONS.map(condition => {
                  const selected = selections[part] === condition;
                  const { textColor, selectedBg } = CONDITION_META[condition];
                  return (
                    <div key={condition} className="flex-1 px-0.5">
                      <button
                        type="button"
                        onClick={() => handleSelect(part, condition)}
                        className={classNames(
                          "h-10 w-full flex items-center gap-1.5 px-2 rounded-lg border transition-colors cursor-pointer",
                          selected
                            ? `${selectedBg} border-transparent`
                            : "bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-100"
                        )}
                      >
                        {selected
                          ? <CheckCircleIcon className="h-4 w-4 shrink-0 flex-none text-white" />
                          : <div className="h-4 w-4 shrink-0 flex-none rounded-full border-2 border-gray-300" />
                        }
                        <span className={classNames(
                          "text-xs font-medium leading-tight",
                          selected ? "text-white" : textColor
                        )}>
                          {condition}
                        </span>
                      </button>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Note preview */}
        <div className="mt-4 p-3 bg-gray-50 rounded-md border border-gray-200">
          <p className="text-xs font-medium text-gray-400 mb-1">Note preview</p>
          {previewNote
            ? <p className="text-sm font-medium text-gray-800">{previewNote}</p>
            : <p className="text-sm text-gray-400 italic">No conditions selected — nothing will be saved.</p>
          }
        </div>

        <div className="mt-4 flex justify-end gap-3">
          <ButtonInput
            label="Cancel"
            isSubmit={false}
            onClick={() => setOpen(false)}
            classes="!bg-white !text-gray-700 !border !border-gray-300 hover:!bg-gray-50"
          />
          <ButtonInput
            label={previewNote ? "Add Note" : "Close"}
            isSubmit={false}
            onClick={handleConfirm}
            isLoading={create.isLoading}
            classes={undefined}
          />
        </div>

      </div>
    </Modal>
  );
}
